import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, type ReactNode } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { AccountShell } from './account/AccountShell'
import { OverviewPage } from './account/OverviewPage'
import { PersonalInfoPage } from './account/PersonalInfoPage'
import { SecurityPage } from './account/SecurityPage'
import { AuthSessionPage } from './auth/AuthSessionPage'
import { VerificationPendingPage } from './auth/VerificationPendingPage'
import { copy } from './lib/i18n'

/**
 * Entrada de login do cliente = Custom Domain (via Application Login URI / Worker).
 * A SPA só recebe a sessão já autenticada (/auth/session) ou inicia UL no CD.
 */
function LoginGate() {
  const { isLoading, isAuthenticated, loginWithRedirect, error } = useAuth0()

  useEffect(() => {
    if (isLoading || isAuthenticated || error) return
    // Sem sessão: manda ao UL no Custom Domain (não usa workers como UI)
    void loginWithRedirect({
      appState: { returnTo: '/account' },
      authorizationParams: {
        organization: import.meta.env.VITE_AUTH0_ORGANIZATION || undefined,
      },
    })
  }, [isLoading, isAuthenticated, error, loginWithRedirect])

  if (error) {
    return (
      <main className="login-page">
        <h1>{copy.errors.auth}</h1>
        <p>{copy.errors.offline}</p>
        <button
          className="primary"
          type="button"
          onClick={() =>
            void loginWithRedirect({
              appState: { returnTo: '/account' },
            })
          }
        >
          {copy.common.retry}
        </button>
      </main>
    )
  }

  if (isAuthenticated) {
    return <Navigate to="/account" replace />
  }

  return (
    <main className="login-page">
      <p>Redirecionando para o login Alelo…</p>
    </main>
  )
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated } = useAuth0()
  if (isLoading) {
    return (
      <main className="login-page">
        <p>{copy.common.loading}</p>
      </main>
    )
  }
  if (!isAuthenticated) return <Navigate to="/" replace />
  return children
}

export function App() {
  return (
    <Routes>
      <Route path="/auth/session" element={<AuthSessionPage />} />
      <Route path="/verificacao-pendente" element={<VerificationPendingPage />} />
      <Route path="/" element={<LoginGate />} />
      <Route
        path="/account"
        element={
          <RequireAuth>
            <AccountShell />
          </RequireAuth>
        }
      >
        <Route index element={<OverviewPage />} />
        <Route path="personal-info" element={<PersonalInfoPage />} />
        <Route path="security" element={<SecurityPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
